import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { OnboardingState, OnboardingActions, TourType, UserPreferences } from '../types/onboarding';
import * as tourAPI from '../api/tours';

interface OnboardingStore extends OnboardingState, OnboardingActions {}

const defaultPreferences: UserPreferences = {
  showOnLogin: true,
  autoStartTours: true,
  disableAnimation: false
};

const initialState: OnboardingState = {
  run: false,
  stepIndex: 0,
  steps: [],
  tourType: null,
  completedTours: [],
  skippedTours: [],
  isOnboardingEnabled: true,
  userPreferences: defaultPreferences,
  lastCompletedAt: null
};

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      startTour: (tourType: TourType) => {
        const { isOnboardingEnabled } = get();

        // Don't start if onboarding is disabled
        if (!isOnboardingEnabled) return;

        set({
          tourType,
          run: true,
          stepIndex: 0
        });
      },

      stopTour: () => {
        set({
          run: false,
          tourType: null,
          stepIndex: 0,
          steps: []
        });
      },

      resetTour: () => {
        set({
          run: true,
          stepIndex: 0
        });
      },

      skipTour: async (tourType: TourType) => {
        set((state) => ({
          completedTours: state.completedTours.includes(tourType)
            ? state.completedTours
            : [...state.completedTours, tourType],
          skippedTours: state.skippedTours.includes(tourType)
            ? state.skippedTours
            : [...state.skippedTours, tourType],
          lastCompletedAt: new Date().toISOString(),
          run: false,
          tourType: null,
          stepIndex: 0
        }));

        // The backend treats a skipped tour as completed too.
        try {
          await tourAPI.completeTour(tourType);
        } catch (error) {
          console.warn(`Failed to sync skip for tour ${tourType}:`, error);
        }
      },

      completeTour: async (tourType: TourType) => {
        set((state) => ({
          completedTours: state.completedTours.includes(tourType)
            ? state.completedTours
            : [...state.completedTours, tourType],
          skippedTours: state.skippedTours.filter((t) => t !== tourType),
          lastCompletedAt: new Date().toISOString(),
          run: false,
          tourType: null,
          stepIndex: 0
        }));

        try {
          await tourAPI.completeTour(tourType);
        } catch (error) {
          console.warn(`Failed to sync completion for tour ${tourType}:`, error);
        }
      },

      setStepIndex: (index: number) => {
        set({ stepIndex: index });
      },

      toggleOnboarding: (enabled: boolean) => {
        set({ isOnboardingEnabled: enabled });
        if (!enabled) {
          set({ run: false, tourType: null, stepIndex: 0 });
        }
      },

      updatePreferences: (preferences: Partial<UserPreferences>) => {
        set((state) => ({
          userPreferences: {
            ...state.userPreferences,
            ...preferences
          }
        }));
      },

      resetAllTours: async () => {
        set({
          completedTours: [],
          skippedTours: [],
          lastCompletedAt: null,
          run: false,
          tourType: null,
          stepIndex: 0
        });

        // Sync reset with backend
        try {
          await tourAPI.resetAllTours();
        } catch (error) {
          console.warn('Failed to sync reset all tours with backend:', error);
          // Continue even if API call fails - local state is already updated
        }
      },

      isTourCompleted: (tourType: TourType) => {
        return get().completedTours.includes(tourType);
      },

      isTourSkipped: (tourType: TourType) => {
        return get().skippedTours.includes(tourType);
      },

      /**
       * Load tour progress from backend and sync with local state
       */
      syncTourProgressFromBackend: async () => {
        try {
          const response = await tourAPI.getTourProgress();
          if (response.status && response.tours) {
            const backendCompletedTours = tourAPI.convertBackendToursToFrontend(response.tours);
            set((state) => ({
              // Keep local progress, including skips, when the backend has older data.
              completedTours: [...new Set([
                ...state.completedTours,
                ...state.skippedTours,
                ...backendCompletedTours
              ])]
            }));
          }
        } catch (error) {
          console.warn('Failed to sync tour progress from backend:', error);
          // Continue with local state if backend sync fails
        }
      }
    }),
    {
      name: 'sawport-onboarding-storage',
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<OnboardingStore>;
        return {
          ...currentState,
          ...persisted,
          completedTours: [...new Set([
            ...(persisted.completedTours ?? []),
            ...(persisted.skippedTours ?? [])
          ])]
        };
      },
      partialize: (state) => ({
        completedTours: state.completedTours,
        skippedTours: state.skippedTours,
        isOnboardingEnabled: state.isOnboardingEnabled,
        userPreferences: state.userPreferences,
        lastCompletedAt: state.lastCompletedAt
      })
    }
  )
);
